import { WorkproductDocument } from "@/api/models/api_specific/workproduct_documents";
import fetchWithAuth from "@/api/secureFetch";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

export interface WorkProductDocumentsResponse {
    documents: WorkproductDocument[];
    active_version: number;
    multiple_versions: boolean;
    served_version_source?: "requested" | "latest";
}

export async function fetchWorkProductDocuments(
    user_id: number,
    project_id: number,
    workProductId: number,
    version: number,
    feature_id: string | number,
    token: string,
    user_story_id?: string
): Promise<WorkProductDocumentsResponse> {
    console.log("fetchWorkProductDocuments called with:", { project_id, workProductId, feature_id, user_story_id });

    // Build query parameters - only include featureId if it has a valid value
    let url = `${BACKEND_URI}/api/workproduct/documents?userId=${user_id}&projectId=${project_id}&workproductId=${workProductId}&version=${version}`;

    // Only add featureId if it's a valid non-empty value
    if (feature_id && feature_id !== "" && feature_id !== "0") {
        url += `&featureId=${feature_id}`;
    }

    // Add user_story_id if provided
    if (user_story_id) {
        url += `&userStoryId=${user_story_id}`;
    }

    console.log("Fetching documents from URL:", url);

    const response = await fetchWithAuth(url, token);

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const responseJson = await response.json();
    return responseJson;
}

export async function fetchWorkProductTitle(projectId: number, workProductId: number, token: string): Promise<string> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/title?projectId=${projectId}&workproductId=${workProductId}`, token);

    if (!response.ok) {
        const responseJson = await response.json();
        const errorMessage = responseJson?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const responseJson = await response.json();
    return responseJson.title;
}

export async function toggleWorkProductCompleted(userId: number, projectId: number, workProductId: number, token: string): Promise<void> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/toggle-completed`, token, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            userId,
            projectId,
            workproductId: workProductId
        })
    });

    if (!response.ok) {
        const responseJson = await response.json();
        const errorMessage = responseJson?.error || response.statusText;
        throw new Error(errorMessage);
    }
}

export type WorkProductReviewRequest = {
    projectId: number;
    workproductId: number;
    accepted: boolean;
    featureId?: number | null;
    userStoryId?: string | null;
};

export type WorkProductReviewResponse = {
    projectId: number;
    workproductId: number;
    featureId: number | null;
    userStoryId: string | null;
    documentVersion: number;
    approved: boolean;
    reviewedAt: string;
};

export async function setWorkProductReview(
    review: WorkProductReviewRequest,
    token: string
): Promise<WorkProductReviewResponse> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/review`, token, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(review)
    });

    if (!response.ok) {
        const responseJson = await response.json();
        const errorMessage = responseJson?.error || response.statusText;
        throw new Error(errorMessage);
    }

    return await response.json();
}

export async function updateDocumentContent(
    warehouseId: number,
    content: string,
    token: string
): Promise<{ success: boolean; warehouse_id?: number; error?: string }> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/document/update-content`, token, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            warehouseId: warehouseId,
            content: content
        })
    });

    if (!response.ok) {
        const responseJson = await response.json();
        const errorMessage = responseJson?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const responseJson = await response.json();
    return responseJson;
}

export async function syncWorkProductCodeArtifact(
    payload: {
        projectId: number;
        workproductId: number;
        featureId?: number;
        userStoryId?: string;
        version?: number;
    },
    token: string
): Promise<{
    synced: boolean;
    reason?: string;
    fileId?: number;
    path?: string;
    contentInserted?: boolean;
}> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/code-artifact/sync`, token, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const responseJson = await response.json();
        const errorMessage = responseJson?.error || response.statusText;
        throw new Error(errorMessage);
    }

    return await response.json();
}
